import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { DEFAULT_APP_CONFIG } from '../config/defaultAppConfig';
import { getAppConfig, getBreedDatabase } from '../services/api';
import { Recipe, Protein } from '../types';
import { AppConfigResponse, RuntimeProductFunnel } from '../types/config';
import { normalizeProtein } from '../utils/normalize';
import { buildProteinAliasLookup } from '../utils/proteinAliases';
import { detectPlatform, CheckoutPlatform } from '../utils/detectPlatform';

/** Positive client-side origin signal ONLY — an explicit URL `?platform=` or the
 *  patitasco hostname. Returns null when there is no signal, so /app-config omits
 *  the param and the BACKEND applies its configured platform_selection (mode /
 *  detect_fallback). Sending a client-side default here would be read by the
 *  backend as an explicit request and override the operator's default channel. */
const detectClientPlatformHint = (): CheckoutPlatform | null => {
  const urlPlatform = new URLSearchParams(window.location.search).get('platform');
  if (urlPlatform === 'shopify' || urlPlatform === 'prestashop') return urlPlatform;
  if ((window.location.hostname || '').toLowerCase().includes('patitasco')) return 'prestashop';
  return null;
};

/** Concrete client-side platform (never null). Only the last-resort fallback for
 *  an older backend that doesn't echo effective_platform. */
const detectClientPlatform = (): CheckoutPlatform =>
  detectPlatform({ search: window.location.search, hostname: window.location.hostname });

export type { CheckoutPlatform };

/**
 * Estado de la config remota:
 * - 'loading': primera carga en curso — el funnel no debe renderizarse aún.
 * - 'ready': hay config real del backend (si luego falla un refresh, se
 *   conserva la última config buena y se sigue en 'ready').
 * - 'unavailable': nunca llegó config remota — el funnel debe mostrar la
 *   pantalla de "estamos actualizando", NUNCA datos por defecto.
 */
export type ConfigStatus = 'loading' | 'ready' | 'unavailable';

interface AppConfigContextType {
  config: AppConfigResponse;
  recipes: Recipe[];
  isLoading: boolean;
  configStatus: ConfigStatus;
  lastError: string;
  platform: CheckoutPlatform;
  logoImage: string;
  refreshConfig: (options?: { silent?: boolean }) => Promise<void>;
  t: (key: string, fallback: string, params?: Record<string, string | number>) => string;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

let appConfigFallbackWarningShown = false;

const isAppConfigNetworkError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.config?.url === '/app-config' &&
  (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response);

const buildRecipeFromProduct = (product: RuntimeProductFunnel, proteinAliasLookup: Record<string, string>): Recipe => {
  // La ficha de receta viene ÍNTEGRAMENTE del backend (catálogo en la BD + sync
  // por canal). Ya no hay fallback hardcodeado por variant; los `|| default` de
  // abajo son solo guardas mínimas contra una fila malformada, nunca fuente de
  // datos reales de receta. Si un dato sale mal, se corrige en la BD.
  // Channel-agnostic recipe key = the canonical product.id (UUID). The backend
  // funnel/checkout contract keys everything by product.id; the Shopify variant
  // id is kept separately (below) only as Shopify support data for mascotas_json.
  return {
    // Sin handle, la clave/URL cae al UUID canónico (product.id) — nunca a un
    // sufijo "variant_" ni al shopify_variant_id (channel-agnostic).
    id: product.handle || product.id,
    handle: product.handle || product.id,
    name: product.display_name || product.name || 'Receta',
    proteinType: normalizeProtein(product.protein_type, proteinAliasLookup, Protein.POLLO),
    // Recetas multi-proteína: getAvailableRecipes prioriza proteinTypes/allergens
    // sobre proteinType (singular) para el gate de alergias. Sin mapear estos
    // dos, una receta "Ternera y Pavo" solo se filtraba por la primera proteína
    // → un alérgico al pavo podía seleccionarla (el backend rebota en Step13,
    // pero el gate cliente tenía el agujero). Hoy las cocinadas son mono-proteína
    // pero esto lo blinda para cualquier receta de 2+ proteínas futura.
    proteinTypes:
      product.protein_types && product.protein_types.length > 0
        ? (product.protein_types.map((pt) => normalizeProtein(pt, proteinAliasLookup, Protein.POLLO)) as Protein[])
        : undefined,
    allergens: product.allergens && product.allergens.length > 0 ? product.allergens : undefined,
    kcalPer100g: product.kcal_per_100g || 120,
    servingSize: product.serving_size_g || 400,
    pricePerUnit: product.price_eur || 3.2,
    imageUrl: product.image_url || '',
    tags: product.tags && product.tags.length > 0 ? product.tags : ['natural'],
    nutritionFacts: product.nutrition_facts || {
      protein: product.protein_percentage || 10,
      fat: product.fat_percentage || 5,
      fiber: 0.5,
      moisture: 78,
    },
    ingredients: product.ingredients || 'Ingredientes naturales.',
    benefits: product.benefits && product.benefits.length > 0 ? product.benefits : ['Alimentación natural'],
    // Canonical UUID key of the funnel/checkout.
    productId: product.id,
    // Effective-channel variant id (data for mascotas_json, a Shopify cart attribute):
    // on Shopify it is the Shopify variant; '' on PrestaShop, where mascotas_json is
    // unused. Never falls back to product.id.
    shopifyVariantId: product.external_variant_id || '',
    isOutOfStock: product.stock_status === 'out_of_stock',
    stockAvailableQty: product.stock_available_qty ?? null,
    recipeGroup: product.recipe_group || undefined,
    packSizeG: product.pack_size_g === 400 || product.pack_size_g === 800 ? product.pack_size_g : undefined,
    lifeStage:
      product.life_stage === 'adult' || product.life_stage === 'puppy' || product.life_stage === 'all'
        ? product.life_stage
        : undefined,
  };
};

/**
 * Group cocinada products by recipe_group, pairing the 400g and 800g variants
 * into a single Recipe with a `variants` field. Uses the 400g product as base.
 */
const groupCocinadaRecipes = (recipes: Recipe[]): Recipe[] => {
  if (!recipes.some((r) => r.recipeGroup)) return recipes;

  const cocinadas = recipes.filter((r) => r.recipeGroup);

  const groups = new Map<string, Recipe[]>();
  for (const r of cocinadas) {
    const key = r.recipeGroup!;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  const paired: Recipe[] = [];
  for (const [, members] of Array.from(groups.entries())) {
    const small = members.find((m: Recipe) => m.packSizeG === 400);
    const large = members.find((m: Recipe) => m.packSizeG === 800);
    const base = small || members[0];
    // La clave canónica (product.id que se persiste en selectedRecipes y viaja a
    // /calculate-plan) apunta a una talla CON stock: cada talla es un Product
    // independiente, así que con el 400g agotado y el 800g disponible el grupo se
    // sigue ofreciendo y se calcula sobre el 800g. Se prefiere el 400g cuando tiene
    // stock (talla por defecto); si no, el 800g; si ninguno, la base (grupo agotado).
    const canonicalMember = small && !small.isOutOfStock ? small : large && !large.isOutOfStock ? large : base;
    // Solo exponemos los tamaños que EXISTEN de verdad como producto — nunca
    // fabricamos una variante fantasma copiando los datos de otro tamaño (eso
    // ofrecía un 800g que el catálogo no tenía). Los valores de display siguen
    // teniendo su fallback por-campo aparte; el set de tamaños no.
    const variants: NonNullable<Recipe['variants']> = {};
    if (small) {
      variants['400g'] = {
        productId: small.productId,
        shopifyVariantId: small.shopifyVariantId,
        priceEur: small.pricePerUnit,
        isOutOfStock: small.isOutOfStock,
        stockAvailableQty: small.stockAvailableQty,
      };
    }
    if (large) {
      variants['800g'] = {
        productId: large.productId,
        shopifyVariantId: large.shopifyVariantId,
        priceEur: large.pricePerUnit,
        isOutOfStock: large.isOutOfStock,
        stockAvailableQty: large.stockAvailableQty,
      };
    }
    paired.push({
      ...base,
      // Display del 400g base (nombre/kcal/imagen son iguales por talla); la clave
      // canónica apunta a la talla con stock (canonicalMember).
      productId: canonicalMember.productId,
      // El grupo está agotado solo si TODAS sus variantes (400g/800g) lo están.
      isOutOfStock: members.every((m: Recipe) => m.isOutOfStock),
      variants,
    });
  }

  return paired;
};

const mergeWithDefaults = (incoming?: AppConfigResponse): AppConfigResponse => {
  if (!incoming) return DEFAULT_APP_CONFIG;
  return {
    ...DEFAULT_APP_CONFIG,
    ...incoming,
    pricing_rules: {
      ...DEFAULT_APP_CONFIG.pricing_rules,
      ...(incoming.pricing_rules || {}),
    },
    nutrition_rules: {
      ...DEFAULT_APP_CONFIG.nutrition_rules,
      ...(incoming.nutrition_rules || {}),
      k_model: {
        ...DEFAULT_APP_CONFIG.nutrition_rules.k_model,
        ...(incoming.nutrition_rules?.k_model || {}),
        k_activity: {
          ...DEFAULT_APP_CONFIG.nutrition_rules.k_model.k_activity,
          ...(incoming.nutrition_rules?.k_model?.k_activity || {}),
        },
        k_bcs: {
          ...DEFAULT_APP_CONFIG.nutrition_rules.k_model.k_bcs,
          ...(incoming.nutrition_rules?.k_model?.k_bcs || {}),
        },
        // k_age_tiers es una lista ordenada: se reemplaza entera, no se fusiona
        // por índice (fusionar tramos daría escaleras incoherentes).
        k_age_tiers:
          incoming.nutrition_rules?.k_model?.k_age_tiers ?? DEFAULT_APP_CONFIG.nutrition_rules.k_model.k_age_tiers,
      },
    },
    inventory_rules: {
      ...DEFAULT_APP_CONFIG.inventory_rules,
      ...(incoming.inventory_rules || {}),
    },
    geo_rules: {
      ...DEFAULT_APP_CONFIG.geo_rules,
      ...(incoming.geo_rules || {}),
    },
    donation_rules: (() => {
      // El backend puede devolver null explícito o string vacío para los
      // VARIANTS no configurados — ambos casos deben preservar el default de
      // defaultAppConfig (variants de Patitas&Rescue). Antes filtraba solo
      // null/undefined y un '' pisaba el default haciendo que el checkout
      // intentara crear líneas con variant_id vacío.
      //
      // PERO el resto de campos del backend (enabled, min_eur, max_eur,
      // info_url) SIEMPRE mandan. Antes el merge solo copiaba
      // recurring_variant_id, así que descartaba el kill switch
      // donation_rules.enabled=false y el widget de donación seguía
      // apareciendo en el funnel aunque estuviera desactivado en backoffice.
      const isMeaningful = (v: unknown): boolean => {
        if (v == null) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        return true;
      };
      const inc = incoming.donation_rules || {};
      const incOneTime = inc.one_time_variant_ids || {};
      const cleanedOneTime = Object.fromEntries(Object.entries(incOneTime).filter(([, v]) => isMeaningful(v)));
      // Copiamos todos los escalares del backend (enabled incluido) y solo
      // tratamos aparte los variants (manejo null-safe arriba).
      const cleanedRest: Partial<typeof inc> = { ...inc };
      delete cleanedRest.one_time_variant_ids;
      if (!isMeaningful(inc.recurring_variant_id)) {
        delete cleanedRest.recurring_variant_id;
      }
      return {
        ...DEFAULT_APP_CONFIG.donation_rules,
        ...cleanedRest,
        one_time_variant_ids: {
          ...DEFAULT_APP_CONFIG.donation_rules.one_time_variant_ids,
          ...cleanedOneTime,
        },
      };
    })(),
    multipet_rules: {
      ...DEFAULT_APP_CONFIG.multipet_rules,
      ...(incoming.multipet_rules || {}),
    },
    runtime_controls: {
      ...DEFAULT_APP_CONFIG.runtime_controls,
      ...(incoming.runtime_controls || {}),
      banner: {
        ...DEFAULT_APP_CONFIG.runtime_controls.banner,
        ...(incoming.runtime_controls?.banner || {}),
        desktop_texts:
          incoming.runtime_controls?.banner?.desktop_texts !== undefined
            ? incoming.runtime_controls.banner.desktop_texts
            : DEFAULT_APP_CONFIG.runtime_controls.banner.desktop_texts,
        mobile_texts:
          incoming.runtime_controls?.banner?.mobile_texts !== undefined
            ? incoming.runtime_controls.banner.mobile_texts
            : DEFAULT_APP_CONFIG.runtime_controls.banner.mobile_texts,
      },
    },
    contact_rules: {
      ...DEFAULT_APP_CONFIG.contact_rules,
      ...(incoming.contact_rules || {}),
    },
    chat_widget_rules: {
      ...DEFAULT_APP_CONFIG.chat_widget_rules,
      ...(incoming.chat_widget_rules || {}),
      whatsapp: {
        ...DEFAULT_APP_CONFIG.chat_widget_rules.whatsapp,
        ...(incoming.chat_widget_rules?.whatsapp || {}),
        visible_steps:
          incoming.chat_widget_rules?.whatsapp?.visible_steps !== undefined
            ? incoming.chat_widget_rules.whatsapp.visible_steps
            : DEFAULT_APP_CONFIG.chat_widget_rules.whatsapp.visible_steps,
      },
    },
    shipping_rules: {
      ...DEFAULT_APP_CONFIG.shipping_rules,
      ...(incoming.shipping_rules || {}),
      blocked_postal_code_patterns:
        incoming.shipping_rules?.blocked_postal_code_patterns !== undefined
          ? incoming.shipping_rules.blocked_postal_code_patterns
          : DEFAULT_APP_CONFIG.shipping_rules.blocked_postal_code_patterns,
      countries:
        incoming.shipping_rules?.countries !== undefined
          ? incoming.shipping_rules.countries
          : DEFAULT_APP_CONFIG.shipping_rules.countries,
    },
    funnel_config: {
      ...DEFAULT_APP_CONFIG.funnel_config,
      ...(incoming.funnel_config || {}),
      dog_breeds:
        incoming.funnel_config?.dog_breeds && incoming.funnel_config.dog_breeds.length > 0
          ? incoming.funnel_config.dog_breeds
          : DEFAULT_APP_CONFIG.funnel_config.dog_breeds,
      pathology_options:
        incoming.funnel_config?.pathology_options && incoming.funnel_config.pathology_options.length > 0
          ? incoming.funnel_config.pathology_options
          : DEFAULT_APP_CONFIG.funnel_config.pathology_options,
      protein_allergy_options:
        incoming.funnel_config?.protein_allergy_options && incoming.funnel_config.protein_allergy_options.length > 0
          ? incoming.funnel_config.protein_allergy_options
          : DEFAULT_APP_CONFIG.funnel_config.protein_allergy_options,
      allowed_product_ids_perro: incoming.funnel_config?.allowed_product_ids_perro || [],
      translations: {
        ...DEFAULT_APP_CONFIG.funnel_config.translations,
        ...(incoming.funnel_config?.translations || {}),
      },
    },
    recipe_recommendation_rules: {
      ...DEFAULT_APP_CONFIG.recipe_recommendation_rules,
      ...(incoming.recipe_recommendation_rules || {}),
      scoring_weights: {
        ...DEFAULT_APP_CONFIG.recipe_recommendation_rules.scoring_weights,
        ...(incoming.recipe_recommendation_rules?.scoring_weights || {}),
      },
      breed_size_ranges: {
        ...DEFAULT_APP_CONFIG.recipe_recommendation_rules.breed_size_ranges,
        ...(incoming.recipe_recommendation_rules?.breed_size_ranges || {}),
      },
      breed_size_overrides: {
        ...DEFAULT_APP_CONFIG.recipe_recommendation_rules.breed_size_overrides,
        ...(incoming.recipe_recommendation_rules?.breed_size_overrides || {}),
      },
      recipe_profiles: {
        ...DEFAULT_APP_CONFIG.recipe_recommendation_rules.recipe_profiles,
        ...(incoming.recipe_recommendation_rules?.recipe_profiles || {}),
      },
    },
    supplement_rules: {
      ...DEFAULT_APP_CONFIG.supplement_rules,
      ...(incoming.supplement_rules || {}),
      scoring_weights: {
        ...DEFAULT_APP_CONFIG.supplement_rules.scoring_weights,
        ...(incoming.supplement_rules?.scoring_weights || {}),
      },
      supplements:
        incoming.supplement_rules?.supplements && incoming.supplement_rules.supplements.length > 0
          ? incoming.supplement_rules.supplements
          : DEFAULT_APP_CONFIG.supplement_rules.supplements,
    },
    breed_database: {
      ...DEFAULT_APP_CONFIG.breed_database,
      ...(incoming.breed_database || {}),
      breeds: {
        ...DEFAULT_APP_CONFIG.breed_database.breeds,
        ...(incoming.breed_database?.breeds || {}),
      },
    },
    products: incoming.products || [],
  };
};

export const AppConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfigResponse>(DEFAULT_APP_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState<ConfigStatus>('loading');
  const [lastError, setLastError] = useState('');

  const refreshConfig = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const runtimeConfig = await getAppConfig(detectClientPlatformHint());
      // Un 200 con body que no es la config (HTML del catch-all de nginx en
      // un deploy, body vacío...) NO puede marcar 'ready': mergeWithDefaults
      // lo convertiría en DEFAULT_APP_CONFIG entero y bypasearía el gate.
      if (
        !runtimeConfig ||
        typeof runtimeConfig !== 'object' ||
        Array.isArray(runtimeConfig) ||
        !(runtimeConfig as AppConfigResponse).pricing_rules
      ) {
        throw new Error('Respuesta de /app-config con shape inválido');
      }
      // breed_database se sirve en su propio endpoint (se sacó de /app-config para
      // aligerar el payload del poll). Se pide una vez (cacheado) y se fusiona aquí
      // para que los consumidores sigan leyendo config.breed_database igual. Si falla
      // queda null y mergeWithDefaults cae al breed_database por defecto.
      const breedDatabase = await getBreedDatabase();
      if (breedDatabase) {
        runtimeConfig.breed_database = breedDatabase;
      }
      const merged = mergeWithDefaults(runtimeConfig);
      // Solo actualizamos el estado si el config cambió de verdad. El polling
      // cada 60s y el refresh en focus llamaban a setConfig incondicionalmente,
      // creando un objeto nuevo y re-renderizando todo el árbol (funnel/admin)
      // cada minuto aunque los datos fueran idénticos (auditoría 2026-06-09).
      setConfig((prev) => {
        try {
          if (prev && JSON.stringify(prev) === JSON.stringify(merged)) {
            return prev; // sin cambios → misma identidad → sin re-render
          }
        } catch {
          /* si la serialización falla, actualizamos igual */
        }
        return merged;
      });
      setLastError('');
      setConfigStatus('ready');
    } catch (error) {
      if (isAppConfigNetworkError(error)) {
        if (process.env.NODE_ENV === 'development' && !appConfigFallbackWarningShown) {
          appConfigFallbackWarningShown = true;
          console.warn('App config remoto no disponible; el funnel queda bloqueado hasta que responda.');
        }
      } else {
        console.error('Error loading app config:', error);
        Sentry.captureException(error);
      }
      setLastError('No se pudo cargar la configuración remota.');
      // Si ya hubo una carga buena, conservamos la última config real (un
      // refresh fallido a mitad de sesión no debe expulsar al usuario).
      // Solo las cargas NO silenciosas (fetch inicial, botón Reintentar)
      // pueden declarar 'unavailable': un poll/focus silencioso que falle
      // rápido mientras el fetch inicial sigue en vuelo no debe flashear la
      // pantalla de error (race verificada en code review 2026-06-10).
      setConfigStatus((prev) => {
        if (prev === 'ready') return 'ready';
        return silent ? prev : 'unavailable';
      });
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshConfig();
  }, [refreshConfig]);

  useEffect(() => {
    const pollEveryMs = 60000;
    const interval = window.setInterval(() => {
      refreshConfig({ silent: true });
    }, pollEveryMs);

    const handleFocus = () => {
      refreshConfig({ silent: true });
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshConfig]);

  const recipes = useMemo(() => {
    const exclusionRules = config.exclusion_rules || {};
    const proteinAliasLookup = buildProteinAliasLookup(exclusionRules.protein_aliases as any);
    const allowedSet = new Set(config.funnel_config.allowed_product_ids_perro || []);
    const hasWhitelist = allowedSet.size > 0;
    const activeProducts = (config.products || []).filter(
      (product) => product.is_active && (!product.product_type || product.product_type === 'recipe'),
    );
    const filtered = hasWhitelist
      ? activeProducts.filter(
          // The whitelist now holds canonical product.ids (UUIDs) — the same key
          // as recipe.productId. The backend (Fase 1) migrated the config.
          (product) => allowedSet.has(product.id),
        )
      : activeProducts;
    // Ya NO se descartan las agotadas: se mapean con `isOutOfStock` para que el
    // funnel las muestre como "Agotado" (Step10 las deshabilita y nunca las
    // auto-selecciona, así el plan/checkout solo ven recetas con stock).
    if (filtered.length > 0) {
      const mapped = filtered.map((product) => buildRecipeFromProduct(product, proteinAliasLookup));
      const grouped = groupCocinadaRecipes(mapped);
      // El funnel es cocinadas-only: la forma del catálogo se decide AQUÍ, una
      // sola vez, para que todas las pantallas (P9, Step10, Step12...) vean el
      // mismo set. Un producto legacy sin recipe_group reactivado por error no
      // debe colarse en unos contadores sí y en otros no.
      return grouped.filter((r) => r.recipeGroup);
    }

    // Sin productos del backend NO se inventan recetas fallback — ni con
    // catálogo vacío ni con error de red (riesgo de vender productos
    // discontinuados a precios viejos). El gate de configStatus en App.tsx
    // impide llegar aquí sin config real.
    return [];
  }, [config.exclusion_rules, config.funnel_config.allowed_product_ids_perro, config.products]);

  const t = useCallback(
    (key: string, fallback: string, params?: Record<string, string | number>) => {
      const template = config.funnel_config.translations?.[key] || fallback;
      if (!params) return template;

      return Object.keys(params).reduce((result, paramKey) => {
        return result.replaceAll(`{${paramKey}}`, String(params[paramKey]));
      }, template);
    },
    [config.funnel_config.translations],
  );

  const platform: CheckoutPlatform = useMemo(
    // The backend is authoritative: it resolved the effective platform from the
    // origin hint we sent on /app-config (honouring force modes). Fall back to
    // client detection for an older backend that doesn't return it.
    () => (config.effective_platform as CheckoutPlatform | undefined) ?? detectClientPlatform(),
    [config],
  );

  const logoImage = useMemo(
    () =>
      platform === 'prestashop'
        ? '/images/funnel/logo-patitas.png'
        : config.funnel_config.translations?.['step1.image.logo'] || '/images/funnel/logo-milolola.png',
    [platform, config.funnel_config.translations],
  );

  const contextValue = useMemo(
    () => ({ config, recipes, isLoading, configStatus, lastError, platform, logoImage, refreshConfig, t }),
    [config, recipes, isLoading, configStatus, lastError, platform, logoImage, refreshConfig, t],
  );

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>;
};

/**
 * Resuelve la imagen de un producto del catálogo (app-config) por el id de variante
 * del canal efectivo (`external_variant_id`; en Shopify coincide con el variant de
 * Shopify que usa Mi Cuenta/Appstle). Devuelve la `image_url` que el backend sirve en
 * cada producto, o undefined si no hay match. Usado por los thumbnails de Mi Cuenta.
 */
export const imageUrlForVariant = (
  products: RuntimeProductFunnel[] | undefined,
  variantId: string,
): string | undefined => {
  if (!products || !variantId) return undefined;
  const product = products.find((p) => p.external_variant_id === variantId);
  return product?.image_url || undefined;
};

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

/**
 * Variante segura: devuelve null si no hay AppConfigProvider en el árbol.
 * Pensada para componentes que pueden renderizarse aisladamente en tests
 * (p.ej. SubscriptionCard de MiCuentaPage). En producción la SPA siempre
 * monta el provider en index.tsx, así que el null sólo ocurre en jsdom.
 */
export const useAppConfigSafe = (): AppConfigContextType | null => {
  const context = useContext(AppConfigContext);
  return context ?? null;
};
